/**
 * Item #4: factory condivisa dell'interceptor di risposta axios per il refresh
 * token su 401. Web (apps/web) e mobile (apps/mobile) avevano due interceptor
 * quasi identici: singleton refreshPromise (coalescing dei 401 concorrenti),
 * flag _retry (una sola ritentata), skip su /auth/refresh e /auth/login,
 * cleanup auth su fallimento. Tutto questo vive ora qui, parametrizzato sui
 * comportamenti specifici di ciascun lato (refresh, set header, logout).
 *
 * NON dipende da axios a runtime: usa tipi strutturali minimi cosi' il pacchetto
 * shared resta agnostico (axios e' dep solo di web/mobile). Ogni lato applica il
 * risultato sul proprio `api.interceptors.response.use(onFulfilled, createRefreshRejectedHandler(...))`.
 */

/**
 * Errore tipizzato del refresh token. Distingue un fallimento DEFINITIVO di
 * autenticazione (`kind: 'auth'` — il server ha risposto 401/403 su
 * /auth/refresh, oppure il refresh token e' assente) da un fallimento
 * TRANSITORIO (`kind: 'network'` — timeout, cold-start, 5xx, rete assente).
 *
 * Principio guida: forzare il re-login (clear token / redirect) SOLO su
 * `kind: 'auth'`. Su `kind: 'network'` la sessione DEVE restare valida e
 * verra' ritentata, cosi' un errore transitorio al boot non slogga l'utente.
 */
export class AuthRefreshError extends Error {
  readonly kind: 'auth' | 'network';
  constructor(kind: 'auth' | 'network', message?: string) {
    super(message ?? `Refresh fallito (${kind})`);
    this.name = 'AuthRefreshError';
    this.kind = kind;
    // Ripristina la prototype chain (necessario quando si estende Error in TS
    // compilato verso target ES5/ES2015 con downlevel di class).
    Object.setPrototypeOf(this, AuthRefreshError.prototype);
  }
}

/** Config minima di una richiesta axios usata dall'handler. */
export interface RetriableRequestConfig {
  url?: string;
  headers?: Record<string, unknown>;
  /** Marker interno: la richiesta e' gia' stata ritentata una volta. */
  _retry?: boolean;
  [key: string]: unknown;
}

/** Errore minimo axios-like consumato dall'handler. */
export interface AxiosLikeError {
  response?: { status?: number };
  config?: RetriableRequestConfig;
  [key: string]: unknown;
}

/** Istanza axios-like: serve solo per ritentare la richiesta originale. */
export interface RequestRunner<TConfig extends RetriableRequestConfig, TResponse> {
  request: (config: TConfig) => Promise<TResponse>;
}

export interface RefreshInterceptorOptions<TConfig extends RetriableRequestConfig, TResponse> {
  /**
   * Esegue il refresh dell'access token e ritorna il nuovo token.
   * NON deve gestire il coalescing: ci pensa la factory col singleton interno.
   */
  refreshFn: () => Promise<string>;
  /** Imposta l'header Authorization (o equivalente) sulla config da ritentare. */
  setAuthHeader: (config: TConfig, token: string) => void;
  /** Istanza con cui ritentare la richiesta originale (di norma `api`). */
  runner: RequestRunner<TConfig, TResponse>;
  /** Cleanup auth (logout) eseguito quando il refresh fallisce o su 401 di /auth/*. */
  onLogout: () => void | Promise<void>;
  /**
   * Path che NON devono innescare il refresh (un 401 qui = logout diretto).
   * Default: ['/auth/refresh', '/auth/login'].
   */
  skipPaths?: string[];
  /**
   * Cosa rigettare quando il refresh fallisce. Differenza preesistente tra i
   * due lati: web rigettava l'errore del refresh, mobile l'errore originale.
   * Default 'refreshError' (comportamento web). Mobile passa 'originalError'
   * per preservare il proprio comportamento osservabile.
   */
  rejectWith?: 'refreshError' | 'originalError';
  /**
   * Classifica un errore di refresh come fallimento di autenticazione
   * DEFINITIVO. `onLogout()` viene chiamato SOLO se questa funzione ritorna
   * true. Su un errore TRANSITORIO (rete/timeout/5xx) NON si slogga: si fa
   * solo il reject (rispettando `rejectWith`), cosi' la sessione resta valida.
   * Default `() => true`: preserva il comportamento preesistente (qualunque
   * fallimento del refresh -> logout).
   */
  isAuthError?: (err: unknown) => boolean;
}

const DEFAULT_SKIP_PATHS = ['/auth/refresh', '/auth/login'];

/**
 * Crea l'handler `onRejected` da passare come secondo argomento a
 * `axiosInstance.interceptors.response.use(onFulfilled, handler)`.
 *
 * Comportamento (identico ai due interceptor originali):
 *  - non-401 o gia' ritentata -> reject pass-through;
 *  - 401 su skipPaths -> onLogout + reject;
 *  - 401 altrove -> refresh (singleton coalescing), set header, retry una volta;
 *  - refresh fallito di AUTENTICAZIONE (isAuthError true) -> onLogout + reject;
 *  - refresh fallito TRANSITORIO (rete/timeout/5xx) -> solo reject, NO logout.
 */
export function createRefreshRejectedHandler<TConfig extends RetriableRequestConfig, TResponse>(
  options: RefreshInterceptorOptions<TConfig, TResponse>,
): (error: AxiosLikeError) => Promise<unknown> {
  const { refreshFn, setAuthHeader, runner, onLogout } = options;
  const skipPaths = options.skipPaths ?? DEFAULT_SKIP_PATHS;
  const rejectWith = options.rejectWith ?? 'refreshError';
  // Default: ogni fallimento del refresh e' trattato come auth (comportamento
  // preesistente). I lati che sanno distinguere passano isAuthError esplicito.
  const isAuthError = options.isAuthError ?? (() => true);

  // Singleton del refresh: piu' 401 concorrenti coalescono in UNA sola chiamata
  // a refreshFn sullo stesso refresh token che ruota (no race -> no logout).
  let refreshPromise: Promise<string> | null = null;

  return async (error: AxiosLikeError): Promise<unknown> => {
    const originalReq = error?.config as TConfig | undefined;
    if (error?.response?.status !== 401 || originalReq?._retry) {
      return Promise.reject(error);
    }
    const url = originalReq?.url ?? '';
    if (skipPaths.some(p => url.includes(p))) {
      await onLogout();
      return Promise.reject(error);
    }
    if (!originalReq) return Promise.reject(error);
    originalReq._retry = true;
    try {
      refreshPromise ??= refreshFn();
      const newToken = await refreshPromise;
      refreshPromise = null;
      setAuthHeader(originalReq, newToken);
      return await runner.request(originalReq);
    } catch (refreshErr) {
      refreshPromise = null;
      // Logout SOLO su fallimento auth definitivo. Su errore transitorio
      // (rete/timeout/5xx) NON sloggare: la sessione resta, si fa solo reject.
      if (isAuthError(refreshErr)) {
        await onLogout();
      }
      // Web rigetta l'errore del refresh; mobile l'errore originale (401).
      return Promise.reject(rejectWith === 'originalError' ? error : refreshErr);
    }
  };
}
